// Apple HealthKit bridge (iOS only).
//
// HealthKit is an iOS-only native API; it cannot be reached from a browser.
// When the app runs inside the KnowMeno Capacitor shell on iOS, this module
// dispatches to a small Swift plugin (`HealthKitBridge`) that wraps the Vital
// iOS SDK. On every other surface (web, Android, SSR) the functions no-op so
// the rest of the app keeps working unchanged.
//
// Data flow:
//   user taps Connect → mint Vital sign-in token via server fn →
//   bridge.configure({userId, token}) → bridge.requestPermissions() →
//   Vital SDK syncs HealthKit → Vital cloud → existing junction-webhook →
//   wearable_metrics rows (same shape as Fitbit/Oura).

type BridgeResult = { ok: boolean; error?: string };

interface HealthKitPlugin {
  configure(opts: {
    userId: string;        // Vital user id (NOT our Supabase uid)
    token: string;         // short-lived Vital sign-in token
    environment: "sandbox" | "production";
    region: "us" | "eu";
  }): Promise<BridgeResult>;
  requestPermissions(): Promise<BridgeResult>;
  syncNow(): Promise<BridgeResult>;
  disconnect(): Promise<BridgeResult>;
}


let cachedPlugin: HealthKitPlugin | null | undefined;

async function getPlugin(): Promise<HealthKitPlugin | null> {
  if (cachedPlugin !== undefined) return cachedPlugin;
  if (typeof window === "undefined") {
    cachedPlugin = null;
    return null;
  }
  try {
    const { Capacitor, registerPlugin } = await import("@capacitor/core");
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") {
      cachedPlugin = null;
      return null;
    }
    cachedPlugin = registerPlugin<HealthKitPlugin>("HealthKitBridge");
    return cachedPlugin;
  } catch {
    cachedPlugin = null;
    return null;
  }
}

export async function isHealthKitAvailable(): Promise<boolean> {
  return (await getPlugin()) !== null;
}

export async function connectAppleHealth(opts: {
  vitalUserId: string;
  vitalToken: string;
  environment: "sandbox" | "production";
  region?: "us" | "eu";
}): Promise<BridgeResult> {
  const plugin = await getPlugin();
  if (!plugin) return { ok: false, error: "HealthKit is only available in the KnowMeno iOS app." };
  const cfg = await plugin.configure({
    userId: opts.vitalUserId,
    token: opts.vitalToken,
    environment: opts.environment,
    region: opts.region ?? "us",
  });
  if (!cfg.ok) return cfg;
  const perm = await plugin.requestPermissions();
  if (!perm.ok) return perm;
  return plugin.syncNow();
}

// Disconnect Apple Health: revoke server-side (deletes the Vital user
// and marks wearable_connections row disconnected) AND sever the local
// HealthKit link via the native plugin. Safe to call from web — server
// revoke runs, native disconnect no-ops.
export async function disconnectAppleHealth(): Promise<BridgeResult> {
  try {
    const { revokeAppleHealthAccess } = await import("@/lib/healthkit.functions");
    await revokeAppleHealthAccess();
  } catch (err) {
    return { ok: false, error: `Server revoke failed: ${err instanceof Error ? err.message : String(err)}` };
  }
  const plugin = await getPlugin();
  if (!plugin) return { ok: true };
  return plugin.disconnect();
}

